// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import { AccessDeniedPage } from '../pages/admin/AccessDeniedPage.js';
import { SkeletonLoader } from './SkeletonLoader.js';
import { apiClient } from '../services/api-client.js';

interface ProtectedRouteProps {
  children: React.ReactNode;
  permission?: string;
  anyPermission?: string[];
}

/**
 * Route wrapper that redirects unauthenticated users.
 * Also redirects to /setup if the setup wizard has not been completed or skipped.
 */
export function ProtectedRoute({ children, permission, anyPermission }: ProtectedRouteProps) {
  const { isAuthenticated, loading } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermissionContext();
  const location = useLocation();
  const [wizardChecked, setWizardChecked] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || loading) return;

    // Skip check when already on the setup page to avoid infinite redirect
    if (location.pathname === '/setup') {
      setWizardChecked(true);
      return;
    }

    apiClient
      .getWizard()
      .then((wizard: any) => {
        const done = wizard?.status === 'completed' || wizard?.status === 'skipped';
        setNeedsSetup(!done);
      })
      .catch(() => {
        // If wizard fetch fails, don't block the user
        setNeedsSetup(false);
      })
      .finally(() => setWizardChecked(true));
  }, [isAuthenticated, loading, location.pathname]);

  if (loading || (isAuthenticated && !wizardChecked)) {
    return <SkeletonLoader variant="card" count={1} />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (needsSetup) {
    return <Navigate to="/setup" replace />;
  }

  // Permission check
  if (permission && !hasPermission(permission)) {
    return <AccessDeniedPage />;
  }

  if (anyPermission && anyPermission.length > 0 && !hasAnyPermission(anyPermission)) {
    return <AccessDeniedPage />;
  }

  return <>{children}</>;
}
